import { useEffect, useRef, useState } from "react";
import { getTokenAsync } from "@coworker/app/src/hooks/API/getAuthToken";
import {
  NotificationBase,
  NotificationTempMapping,
} from "../types/notification.types";
import { SseNotifications } from "../service/SseNotification";
import { useQueryClient } from "@tanstack/react-query";
import { updateQueryCache } from "./updateQueryCache";
import { CORE_SERVICE_URL } from "@coworker/app/src/hooks/API/core.service";
import { useOnline } from "@coworker/app/src/core/hooks/useOnline";
import { tempMapping } from "../service/notification.service";

const NOTIFICATIONS_MAX_COUNT = 100;
const SSE_BASE_URL = `${CORE_SERVICE_URL}/notifications/sse`;

export const useSseNotifications = (
  storeId: string,
  uid: string,
  latestNotificationCreatedAt: string
) => {
  const queryClient = useQueryClient();
  const { isOnline } = useOnline();
  const localFetchCreatedAt = useRef<null | string>(null);
  const [fetchCreatedAt, setFetchCreateAt] = useState("");
  const [maxNotificationsCount, setMaxNotificationsCount] = useState(
    NOTIFICATIONS_MAX_COUNT
  );

  // Only set fetchCreatedAt once from render loop
  if (latestNotificationCreatedAt && !fetchCreatedAt) {
    setFetchCreateAt(latestNotificationCreatedAt);
  }

  useEffect(() => {
    if (isOnline && queryClient && uid) {
      const headers = async (): Promise<HeadersInit> => ({
        Authorization: `Bearer ${(await getTokenAsync()) ?? ""}`,
        "Content-Type": "text/event-stream",
      });
      const url = `${SSE_BASE_URL}/stores/${encodeURIComponent(storeId)}/users/${encodeURIComponent(uid)}/messages`;
      const sseNotification = new SseNotifications(url, headers, [
        `createdAt=${encodeURIComponent(fetchCreatedAt)}`,
      ]);
      sseNotification.onmessage = ({ event, data }) => {
        if (event && event.includes("max-notifications-count:")) {
          const maxNotificationsCount = parseInt(
            (event.split(":")[1] ?? "").trim()
          );
          if (!isNaN(maxNotificationsCount)) {
            setMaxNotificationsCount(maxNotificationsCount);
          }
        }

        if (data) {
          const json = JSON.parse(data) as NotificationTempMapping[];
          const notifications = json.map(tempMapping) as NotificationBase[];
          for (const notification of notifications) {
            updateQueryCache(
              queryClient,
              uid,
              notification,
              maxNotificationsCount
            );
            sseNotification.setQueryParams([
              `createdAt=${encodeURIComponent(notification.createdAt)}`,
            ]);
            localFetchCreatedAt.current = notification.createdAt;
          }
        }
      };
      return () => {
        sseNotification.close();
        if (
          localFetchCreatedAt.current &&
          localFetchCreatedAt.current !== fetchCreatedAt
        ) {
          setFetchCreateAt(localFetchCreatedAt.current);
        }
      };
    } else {
      return;
    }
  }, [
    isOnline,
    fetchCreatedAt,
    maxNotificationsCount,
    queryClient,
    storeId,
    uid,
  ]);
  return;
};
